import { ref, watch, computed } from "vue";
class FetchError extends Error {
  status;
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}
import {
  crudHeadersGenerator,
  crudOnUnauthenticated,
  setCrudHeadersGenerator,
  setCrudOnUnauthenticated
} from "../constants/crudAuthInterceptor.js";
import { useRuntimeConfig } from "nuxt/app";
import { useMSAuth } from "./useMSAuth.js";
import { useCrudConverters } from "./useCrudConverters.js";
import { useAlertService } from "./useAlertService.js";
export const useCrudApi = (path, watchPage = true, transformItem = null, transformItems = null) => {
  const atomNuxtConfig = useRuntimeConfig().public.atomNuxt;
  const { stringifyQuery, flattenQuery, cloneDeep } = useCrudConverters();
  const { setAlertMessage } = useAlertService();
  if (atomNuxtConfig.azureAdB2C) {
    setCrudHeadersGenerator(async () => {
      const { acquireTokensSilent } = useMSAuth();
      const tokens = await acquireTokensSilent();
      if (atomNuxtConfig.debug) console.log("Tokens:", tokens);
      return {
        Authorization: `Bearer ${tokens?.idToken}`
      };
    });
    setCrudOnUnauthenticated(async () => {
    });
  }
  const currentPage = ref(1);
  const totalPages = ref(0);
  const totalItems = ref(0);
  const perPage = ref(10);
  const filters = ref([]);
  const items = ref([]);
  const item = ref({});
  const originalItem = ref({});
  const exportUrl = ref(null);
  const count = ref(0);
  const listErrors = ref({});
  const formErrors = ref({});
  const itemErrors = ref({});
  const countErrors = ref({});
  const exportErrors = ref({});
  const listPending = ref(false);
  const formPending = ref(false);
  const itemPending = ref(false);
  const countPending = ref(false);
  const exportPending = ref(false);
  if (watchPage) {
    watch(currentPage, async (newPage) => {
      let finalFilters = filters.value;
      if (filters.value.length > 0) {
        filters.value.forEach((filter) => {
          if (filter.value) {
            finalFilters[filter.key] = filter.value;
          }
        });
      }
      await getItems(null, null, finalFilters);
    });
  }
  const hasFormErrors = computed(() => formErrors.value && Object.keys(formErrors.value).length > 0);
  const hasItemErrors = computed(() => itemErrors.value && Object.keys(itemErrors.value).length > 0);
  const hasListErrors = computed(() => listErrors.value && Object.keys(listErrors.value).length > 0);
  const hasExportErrors = computed(() => exportErrors.value && Object.keys(exportErrors.value).length > 0);
  const hasItem = computed(() => item.value && Object.keys(item.value).length > 0);
  const fetchRequest = async (url, options, pending, errors) => {
    try {
      pending.value = true;
      errors.value = {};
      if (crudHeadersGenerator) {
        const dynamicHeaders = await crudHeadersGenerator();
        options.headers = {
          ...options.headers,
          ...dynamicHeaders
        };
      }
      if (atomNuxtConfig.debug) {
        console.log("Request:", {
          url,
          options
        });
      }
      const response = await fetch(url, options);
      if (atomNuxtConfig.debug) {
        console.log("Response Status:", response.status, response.statusText);
        console.log("Response Headers:", [...response.headers.entries()]);
      }
      if (!response.ok) {
        console.log("Response Failed with status:", response.status);
        if (response.status === 401 && crudOnUnauthenticated) {
          await crudOnUnauthenticated();
        }
        if (atomNuxtConfig.debug) {
          console.log("Response Failed:", response);
        }
        const errorBody = await response.json().catch(() => null);
        errors.value = errorBody || { message: "Failed to parse error response" };
        throw new FetchError(response.statusText, response.status);
      }
      if (atomNuxtConfig.debug) {
        console.log("Response:", response);
      }
      const contentType = response.headers.get("content-type");
      let responseBody;
      if (contentType && contentType.includes("application/json")) {
        responseBody = await response.json();
      } else {
        responseBody = await response.text();
      }
      if (response.headers.get("user-message")) {
        setAlertMessage(response.headers.get("user-message"));
      }
      return responseBody;
    } catch (error) {
      if (error.status === 401 && crudOnUnauthenticated) {
        await crudOnUnauthenticated();
      }
      if (atomNuxtConfig.debug) {
        console.error("Request error:", error);
      }
    } finally {
      pending.value = false;
    }
  };
  const getItems = async (page = null, perPageLimit = null, filterValues = null, headers = {}) => {
    let pageToFetch = page ? page : currentPage.value;
    let perPageToFetch = perPageLimit ? perPageLimit : perPage.value;
    let parameters = stringifyQuery({ page: pageToFetch, limit: perPageToFetch }, "?");
    if (filterValues) {
      const flattenedFilters = flattenQuery({ filters: filterValues });
      const filtersQuery = stringifyQuery(flattenedFilters, "&");
      parameters += filtersQuery;
    }
    const url = `${atomNuxtConfig.apiBaseUrl}/${path}${parameters}`;
    const options = {
      method: "GET",
      headers: {
        ...atomNuxtConfig.additionalHeaders,
        ...headers
      }
    };
    const responseData = await fetchRequest(url, options, listPending, listErrors);
    if (responseData.currentPage) {
      if (transformItems) {
        responseData.items = transformItems(responseData.items);
      }
      if (transformItem) {
        responseData.items = responseData.items.map((item2) => transformItem(item2));
      }
      items.value = responseData.items;
      currentPage.value = responseData.currentPage;
      totalPages.value = responseData.totalPages;
      totalItems.value = responseData.totalItems;
      perPage.value = responseData.perPage;
      filters.value = responseData.filters;
    } else {
      items.value = responseData;
      currentPage.value = 1;
      totalPages.value = 1;
      totalItems.value = responseData.length;
      perPage.value = responseData.length;
      filters.value = [];
    }
  };
  const exportItems = async (filterValues = null) => {
    let parameters = `/export`;
    if (filterValues) {
      const flattenedFilters = flattenQuery({ filters: filterValues });
      const filtersQuery = stringifyQuery(flattenedFilters, "?");
      parameters += filtersQuery;
    }
    const url = `${atomNuxtConfig.apiBaseUrl}/${path}${parameters}`;
    const options = {
      method: "GET",
      headers: {
        ...atomNuxtConfig.additionalHeaders
      }
    };
    const responseData = await fetchRequest(url, options, exportPending, exportErrors);
    exportUrl.value = responseData.url;
  };
  const getCount = async (filterValues = null, headers = {}) => {
    let parameters = `/count`;
    if (filterValues) {
      const flattenedFilters = flattenQuery({ filters: filterValues });
      const filtersQuery = stringifyQuery(flattenedFilters, "?");
      parameters += filtersQuery;
    }
    const url = `${atomNuxtConfig.apiBaseUrl}/${path}${parameters}`;
    const options = {
      method: "GET",
      headers: {
        ...atomNuxtConfig.additionalHeaders,
        ...headers
      }
    };
    const responseData = await fetchRequest(url, options, countPending, countErrors);
    count.value = responseData.count;
  };
  const getItem = async (id, headers = {}) => {
    const url = `${atomNuxtConfig.apiBaseUrl}/${path}/${id}`;
    const options = {
      method: "GET",
      headers: {
        ...atomNuxtConfig.additionalHeaders,
        ...headers
      }
    };
    let res = await fetchRequest(url, options, itemPending, itemErrors);
    if (!hasItemErrors.value) {
      if (transformItem) {
        res = transformItem(res);
      }
      item.value = res;
      originalItem.value = cloneDeep(res);
    }
  };
  const createItem = async (itemData, headers = {}) => {
    const url = `${atomNuxtConfig.apiBaseUrl}/${path}`;
    const options = {
      method: "POST",
      headers: {
        ...atomNuxtConfig.additionalHeaders,
        ...headers
      },
      body: JSON.stringify(itemData)
    };
    let res = await fetchRequest(url, options, formPending, formErrors);
    if (!hasFormErrors.value) {
      if (transformItem) {
        res = transformItem(res);
      }
      item.value = res;
      originalItem.value = cloneDeep(res);
    }
  };
  const updateItem = async (id, itemData, headers = {}) => {
    const url = `${atomNuxtConfig.apiBaseUrl}/${path}/${id}`;
    const options = {
      method: "PUT",
      headers: {
        ...atomNuxtConfig.additionalHeaders,
        ...headers
      },
      body: JSON.stringify(itemData)
    };
    let res = await fetchRequest(url, options, formPending, formErrors);
    if (!hasFormErrors.value) {
      if (transformItem) {
        res = transformItem(res);
      }
      item.value = res;
      originalItem.value = cloneDeep(res);
    }
  };
  const deleteItem = async (id, headers = {}) => {
    const url = `${atomNuxtConfig.apiBaseUrl}/${path}/${id}`;
    const options = {
      method: "DELETE",
      headers: {
        ...atomNuxtConfig.additionalHeaders,
        ...headers
      }
    };
    let res = await fetchRequest(url, options, formPending, formErrors);
    if (!hasFormErrors.value) {
      if (transformItem) {
        res = transformItem(res);
      }
      item.value = res;
      originalItem.value = cloneDeep(res);
    }
  };
  return {
    items,
    currentPage,
    totalPages,
    totalItems,
    perPage,
    filters,
    item,
    originalItem,
    count,
    listErrors,
    exportErrors,
    listPending,
    formErrors,
    countErrors,
    formPending,
    itemErrors,
    itemPending,
    countPending,
    exportPending,
    getItems,
    getItem,
    getCount,
    createItem,
    updateItem,
    deleteItem,
    exportItems,
    exportUrl,
    hasFormErrors,
    hasItemErrors,
    hasListErrors,
    hasExportErrors,
    hasItem
  };
};
